import { Alert, Card, Col, Row, Skeleton, Statistic, Table, Tag, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { errorMessage } from '../api/client';
import { useDealerDashboardSummary } from '../api/hooks';
import type { RecentTransaction } from '../types';
import { date, money } from '../utils/format';

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  'Profit Payment': 'green',
};

/**
 * Company-wide KPIs for the outbound (dealer) book — the mirror of the main Investment Dashboard,
 * kept as its own page the same way that dashboard is separate from the Investors list.
 */
export default function DealerDashboardPage() {
  const { data: summary, isLoading, error } = useDealerDashboardSummary();

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !summary) return <Skeleton active paragraph={{ rows: 8 }} />;

  const kpis = [
    { title: 'Total dealers', value: summary.totalDealers, suffix: `/ ${summary.activeDealers} active` },
    { title: 'Total capital', value: money(summary.totalCapital), prefix: 'PKR' },
    { title: "Today's due profit", value: money(summary.todayDueProfit), prefix: 'PKR' },
    { title: 'Received this month', value: money(summary.thisMonthReceivedProfit), prefix: 'PKR' },
    { title: 'Outstanding profit', value: money(summary.outstandingProfit), prefix: 'PKR' },
    { title: 'Capital + liabilities', value: money(summary.totalCapitalPlusLiabilities), prefix: 'PKR' },
  ];

  return (
    <>
      <Typography.Title level={3}>My Investment Dashboard</Typography.Title>

      <Row gutter={[16, 16]}>
        {kpis.map((kpi) => (
          <Col key={kpi.title} xs={24} sm={12} lg={8} xl={4}>
            <Card>
              <Statistic title={kpi.title} value={kpi.value} prefix={kpi.prefix} suffix={kpi.suffix} valueStyle={{ fontSize: 20 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card title="Profit received — last 12 months" style={{ marginTop: 16 }}>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={summary.monthlyReceivedProfit} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tickLine={false} />
              <YAxis tickFormatter={(v: number) => money(v)} width={90} tickLine={false} />
              <ChartTooltip formatter={(v: number) => `PKR ${money(v)}`} />
              <Bar dataKey="amount" fill="#1677ff" radius={[4, 4, 0, 0]} name="Profit received" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Capital given vs received — last 12 months" style={{ marginTop: 16 }}>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={summary.monthlyCapitalFlow} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tickLine={false} />
              <YAxis tickFormatter={(v: number) => money(v)} width={90} tickLine={false} />
              <ChartTooltip formatter={(v: number) => `PKR ${money(v)}`} />
              <Legend />
              <Bar dataKey="given" fill="#cf1322" radius={[4, 4, 0, 0]} name="Capital given" />
              <Bar dataKey="received" fill="#3f8600" radius={[4, 4, 0, 0]} name="Capital received" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Recent transactions" style={{ marginTop: 16 }}>
        <Table<RecentTransaction>
          dataSource={summary.recentTransactions}
          rowKey={(row, index) => `${row.investorCode}-${row.date}-${index}`}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Date', dataIndex: 'date', render: date },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{value}</Tag>,
            },
            { title: 'Code', dataIndex: 'investorCode' },
            { title: 'Dealer', dataIndex: 'investorName' },
            { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
          ]}
        />
      </Card>
    </>
  );
}
